import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Overview from "@/components/Overview";
import EventOptions from "@/components/EventOptions";
import Pricing from "@/components/Pricing";
import VendorSection, { VendorDialog } from "@/components/VendorSection";
import SponsorSection, { SponsorDialog } from "@/components/SponsorSection";
import Gallery from "@/components/Gallery";
import FamilyFriendly from "@/components/FamilyFriendly";
import EventDetails from "@/components/EventDetails";
import VolunteerSection, { VolunteerDialog } from "@/components/VolunteerSection";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import Awards from "@/components/Awards";
import LiveReel from "@/components/LiveReel";
import PromoVideo from "@/components/PromoVideo";
import CTAStrip from "@/components/CTAStrip";
import WellnessPartners from "@/components/WellnessPartners";
import FuelStation from "@/components/FuelStation";
import { goToRunSignup } from "@/lib/runsignup";

const VALID_INTERESTS = new Set([
  "warm_up", "gym_team", "cheer_zone", "raffle", "wellness_table", "fuel_partner", "team_captain", "general",
]);

export default function Landing() {
  const [vendorOpen, setVendorOpen] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [sponsorLevel, setSponsorLevel] = useState("champion");
  const [sponsorInterest, setSponsorInterest] = useState("general");
  const [volunteerOpen, setVolunteerOpen] = useState(false);

  // Registration + donation now happen entirely on RunSignup
  const openRegister = () => goToRunSignup("register");
  const openDonate = () => goToRunSignup("donate");
  const openVendor = () => setVendorOpen(true);
  const openSponsor = (levelOrInterest = "champion") => {
    // If passed value matches an interest key (e.g. from Wellness/Fuel cards), open with that interest + custom level
    if (VALID_INTERESTS.has(levelOrInterest)) {
      setSponsorInterest(levelOrInterest);
      setSponsorLevel("custom");
    } else {
      setSponsorLevel(levelOrInterest);
      setSponsorInterest("general");
    }
    setSponsorOpen(true);
  };
  const openVolunteer = () => setVolunteerOpen(true);

  // Auto-open sponsor form when URL has ?interest=<key> (for campaign links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const interest = params.get("interest");
    if (interest && VALID_INTERESTS.has(interest)) {
      setSponsorInterest(interest);
      setSponsorLevel("custom");
      setSponsorOpen(true);
    }
    // Also allow ?volunteer=1 / ?vendor=1 / ?register=1 shortcuts
    if (params.get("volunteer") === "1") setVolunteerOpen(true);
    if (params.get("vendor") === "1") setVendorOpen(true);
    if (params.get("register") === "1") goToRunSignup("register");
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white">
      <Navbar onRegister={openRegister} />
      <Hero
        onRegister={openRegister}
        onSponsor={() => openSponsor("champion")}
        onVendor={openVendor}
        onVolunteer={openVolunteer}
      />
      <Overview />
      <PromoVideo onRegister={openRegister} />
      <CTAStrip
        variant="primary"
        onRegister={openRegister}
        onVendor={openVendor}
        onSponsor={() => openSponsor("champion")}
        onVolunteer={openVolunteer}
        onDonate={openDonate}
      />
      <LiveReel />
      <EventOptions onRegister={openRegister} />
      <Pricing />
      <VendorSection onApply={openVendor} />
      <SponsorSection onApply={openSponsor} />
      <FuelStation onSponsor={() => openSponsor("fuel_partner")} />
      <WellnessPartners onSponsor={openSponsor} />
      <Gallery />
      <Awards />
      <FamilyFriendly />
      <CTAStrip
        variant="banner"
        onRegister={openRegister}
        onVendor={openVendor}
        onSponsor={() => openSponsor("champion")}
        onVolunteer={openVolunteer}
        onDonate={openDonate}
      />
      <EventDetails />
      <VolunteerSection onSignup={openVolunteer} />
      <FAQ />
      <Footer onRegister={openRegister} />

      <VendorDialog open={vendorOpen} onOpenChange={setVendorOpen} />
      <SponsorDialog open={sponsorOpen} onOpenChange={setSponsorOpen} initialLevel={sponsorLevel} initialInterest={sponsorInterest} />
      <VolunteerDialog open={volunteerOpen} onOpenChange={setVolunteerOpen} />
    </div>
  );
}
